import React, { useCallback, useMemo, useState } from "react";
import styled from "styled-components";

import Table, { TableColumn } from "../table/Table";
import DateDisplay, {
  DATE_DISPLAY_TYPE,
} from "../date-display/DateDisplay";
import MediaDetailModal from "../media-preview/components/MediaDetailModal";
import ToolTip from "../tooltip/ToolTip";
import {
  flattenAcrRowToMatchRows,
  formatAiSummary,
  getAcrIconMeta,
  getAcrStatusLabel,
} from "../../utils/acr";
import type { AcrMatchRow, AcrResultRow } from "../../types/acr";

/**
 * ACR Cloud 結果一覧の共有テーブル群（管理者ページ / プロジェクト INFO 共通）。
 *
 * 3 区分（ショット内メディア / ショットコメント添付 / トークルーム添付）を
 * それぞれ {@link Table} で描画する。各ファイル行は `acr_scan_result` を
 * 「1 照合結果＝1 行」に平坦化して列展開し、ファイル単位の列（ショット名 /
 * アップロード日時 / ファイル名 / 照合ステータス / AI判定）はマッチ数ぶん
 * 繰り返し表示する。各行はクリックで {@link MediaDetailModal} を開く
 * （照合中の行からは個別の再照合も行える）。
 *
 * 再照合は `enableRescan`（既定 false）で制御し、true のときのみモーダルに
 * `fileId` / `onRescanSuccess` を渡して再照合ボタンを表示する。
 */

const StatusIcon = styled.span.attrs({
  className: "material-symbols-rounded",
})`
  font-size: 18px;
  flex-shrink: 0;
`;

const SortArrow = styled.span.attrs({
  className: "material-symbols-rounded",
})`
  font-size: 16px;
  color: var(--soremo-blue);
`;

const MatchCount = styled.span.attrs({
  className: "bodytext-11 u-text-light-gray",
})``;

/** 照合結果（カテゴリ〜一致時間）の列。3 区分共通。 */
const MATCH_COLUMNS: TableColumn[] = [
  { key: "category", header: "カテゴリ", width: 120 },
  { key: "title", header: "タイトル", minWidth: 160 },
  { key: "artists", header: "アーティスト", minWidth: 140 },
  { key: "score", header: "スコア", width: 76 },
  { key: "isrc", header: "ISRC", width: 130 },
  { key: "time_range", header: "一致区間", width: 130 },
  { key: "duration", header: "一致時間", width: 96 },
];

const STATUS_COLUMN: TableColumn = {
  key: "acr_status",
  header: "照合",
  width: 64,
};
const AI_COLUMN: TableColumn = {
  key: "ai_summary",
  header: "AI判定",
  minWidth: 140,
};

/** ショット系（ショット内メディア / ショットコメント添付）の列定義。 */
const SHOT_COLUMNS: TableColumn[] = [
  { key: "shot_name", header: "ショット名", minWidth: 120 },
  { key: "uploaded_at", header: "アップロード日時", width: 150 },
  { key: "file_name", header: "ファイル名", minWidth: 180 },
  STATUS_COLUMN,
  ...MATCH_COLUMNS,
  AI_COLUMN,
];

/** トークルーム添付用の列定義（ショット名なし）。 */
const TALK_ROOM_COLUMNS: TableColumn[] = [
  { key: "uploaded_at", header: "アップロード日時", width: 150 },
  { key: "file_name", header: "ファイル名", minWidth: 180 },
  STATUS_COLUMN,
  ...MATCH_COLUMNS,
  AI_COLUMN,
];

/**
 * 照合ステータスをプレビュー流用のアイコンのみで表示する。
 * - matched: 青アイコン
 * - pending / processing: グレーアイコン（照合中）
 * - no_match / error: 無印（アイコン無し）
 * ラベルテキストは出さず、ホバー時の ToolTip / aria-label でのみ補足する。
 */
const AcrStatusCell: React.FC<{ status: AcrResultRow["acr_scan_status"] }> = ({
  status,
}) => {
  const icon = getAcrIconMeta(status);
  if (!icon.show) return null;
  const label = getAcrStatusLabel(status)?.label ?? status;
  return (
    <ToolTip description={label}>
      <StatusIcon style={{ color: icon.color }} role="img" aria-label={label}>
        error
      </StatusIcon>
    </ToolTip>
  );
};

/** 照合ステータスの並び替え順（asc で matched を先頭）。 */
const STATUS_SORT_ORDER: Record<string, number> = {
  matched: 0,
  processing: 1,
  pending: 2,
  no_match: 3,
  error: 4,
};

/** "H:MM:SS" / "MM:SS"（区間は開始側）を秒数に変換する。空・不正は "" を返す。 */
const parseTimeToSeconds = (value: string): number | "" => {
  if (!value) return "";
  const head = value.split("-")[0].trim();
  const parts = head.split(":").map((p) => Number(p));
  if (parts.length === 0 || parts.some((p) => Number.isNaN(p))) return "";
  return parts.reduce((acc, p) => acc * 60 + p, 0);
};

/**
 * 並び替え用の比較値を返す。ファイル単位列はその値、照合単位列は先頭マッチの
 * 値を代表として用いる（同一ファイルの行が分断されないようファイル単位で整列）。
 */
const getAcrSortValue = (row: AcrResultRow, key: string): string | number => {
  switch (key) {
    case "shot_name":
      return row.shot_name ?? "";
    case "uploaded_at": {
      const t = row.uploaded_at ? new Date(row.uploaded_at).getTime() : NaN;
      return Number.isNaN(t) ? "" : t;
    }
    case "file_name":
      return row.file_name ?? "";
    case "acr_status":
      return STATUS_SORT_ORDER[row.acr_scan_status] ?? 99;
    case "ai_summary":
      return formatAiSummary(row.acr_scan_result?.ai_detection);
    default: {
      const match = flattenAcrRowToMatchRows(row.acr_scan_result)[0];
      if (!match) return "";
      switch (key) {
        case "category":
          return match.category;
        case "title":
          return match.title;
        case "artists":
          return match.artists;
        case "score": {
          const n = parseInt(match.score, 10);
          return Number.isNaN(n) ? "" : n;
        }
        case "isrc":
          return match.isrc;
        case "time_range":
          return parseTimeToSeconds(match.timeRange);
        case "duration":
          return parseTimeToSeconds(match.duration);
        default:
          return "";
      }
    }
  }
};

/** ファイル行の比較関数を生成する。空値は方向によらず常に末尾へ送る。 */
const makeAcrComparator =
  (key: string, dir: "asc" | "desc") =>
  (a: AcrResultRow, b: AcrResultRow): number => {
    const va = getAcrSortValue(a, key);
    const vb = getAcrSortValue(b, key);
    const aEmpty = va === "";
    const bEmpty = vb === "";
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;
    if (bEmpty) return -1;
    const cmp =
      typeof va === "number" && typeof vb === "number"
        ? va - vb
        : String(va).localeCompare(String(vb), "ja");
    return dir === "asc" ? cmp : -cmp;
  };

export interface AcrResultsTablesProps {
  shotMedia: AcrResultRow[];
  shotCommentMedia: AcrResultRow[];
  talkRoomMedia: AcrResultRow[];
  isFetching?: boolean;
  /** true のときモーダルに fileId/onRescanSuccess を渡し再照合ボタンを表示する。 */
  enableRescan?: boolean;
  /** 再照合成功時のコールバック（一覧の再取得など）。enableRescan=true 時のみ有効。 */
  onRescanSuccess?: () => void;
}

/** 管理者 / メンバー共通の ACR 結果テーブル群。 */
const AcrResultsTables: React.FC<AcrResultsTablesProps> = ({
  shotMedia,
  shotCommentMedia,
  talkRoomMedia,
  isFetching = false,
  enableRescan = false,
  onRescanSuccess,
}) => {
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);

  const handleRowClick = useCallback((row: AcrResultRow) => {
    setSelectedFileId(row.file_id);
  }, []);

  // モーダルに渡す行は常に最新の一覧データから引き直す。ポーリング /
  // refetch 後もクリック時のスナップショットが表示され続けるのを防ぐ。
  const selectedRow = useMemo(() => {
    if (!selectedFileId) return null;
    return (
      [...shotMedia, ...shotCommentMedia, ...talkRoomMedia].find(
        (r) => r.file_id === selectedFileId,
      ) ?? null
    );
  }, [selectedFileId, shotMedia, shotCommentMedia, talkRoomMedia]);

  return (
    <>
      <ResultsTable
        title="ショット内メディア"
        columns={SHOT_COLUMNS}
        rows={shotMedia}
        kind="shot"
        isFetching={isFetching}
        onRowClick={handleRowClick}
      />
      <ResultsTable
        title="ショットコメント添付"
        columns={SHOT_COLUMNS}
        rows={shotCommentMedia}
        kind="shot_comment"
        isFetching={isFetching}
        onRowClick={handleRowClick}
      />
      <ResultsTable
        title="トークルーム添付"
        columns={TALK_ROOM_COLUMNS}
        rows={talkRoomMedia}
        kind="talk_room"
        isFetching={isFetching}
        onRowClick={handleRowClick}
      />

      {selectedRow && (
        <MediaDetailModal
          onClose={() => setSelectedFileId(null)}
          fileName={selectedRow.file_name}
          acrScanResult={selectedRow.acr_scan_result}
          acrScanStatus={selectedRow.acr_scan_status}
          acrScannedAt={selectedRow.acr_scanned_at}
          fileId={enableRescan ? selectedRow.file_id : undefined}
          onRescanSuccess={enableRescan ? onRescanSuccess : undefined}
        />
      )}
    </>
  );
};

interface ResultsTableProps {
  title: string;
  columns: TableColumn[];
  rows: AcrResultRow[];
  kind: "shot" | "shot_comment" | "talk_room";
  isFetching: boolean;
  // eslint-disable-next-line no-unused-vars
  onRowClick: (row: AcrResultRow) => void;
}

/** 1 区分ぶんのテーブル。各ファイルを照合結果ごとの行に展開して描画する。 */
const ResultsTable: React.FC<ResultsTableProps> = ({
  title,
  columns,
  rows,
  kind,
  isFetching,
  onRowClick,
}) => {
  const columnCount = columns.length;
  const showShotName = kind !== "talk_room";
  const tableMinWidth = showShotName ? 1260 : 1140;

  // 列ラベルクリックでの並び替え（同一列の再クリックで昇順 / 降順をトグル）。
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const handleHeaderClick = useCallback((key: string) => {
    setSortKey((prevKey) => {
      if (prevKey === key) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        return prevKey;
      }
      setSortDir("asc");
      return key;
    });
  }, []);

  // ヘッダーにソートハンドラとソート方向の矢印を差し込んだ表示用列定義。
  const displayColumns = useMemo<TableColumn[]>(
    () =>
      columns.map((col) => ({
        ...col,
        headerTitle: "クリックで並び替え",
        onHeaderClick: () => handleHeaderClick(col.key),
        header: (
          <>
            {col.header}
            {sortKey === col.key && (
              <SortArrow aria-hidden="true">
                {sortDir === "asc" ? "arrow_upward" : "arrow_downward"}
              </SortArrow>
            )}
          </>
        ),
      })),
    [columns, sortKey, sortDir, handleHeaderClick],
  );

  const sortedRows = useMemo(() => {
    if (!sortKey) return rows;
    return [...rows].sort(makeAcrComparator(sortKey, sortDir));
  }, [rows, sortKey, sortDir]);

  return (
    <section className="u-col u-w100 u-gap12">
      <div className="u-row u-items-center u-gap8">
        <h3 className="heading-13">{title}</h3>
        <MatchCount>{rows.length}件</MatchCount>
      </div>
      <Table columns={displayColumns} minWidth={tableMinWidth}>
        {isFetching && rows.length === 0 ? (
          <tr>
            <td colSpan={columnCount}>
              <p className="bodytext-13 u-text-light-gray u-text-center u-ptb16">
                読み込み中...
              </p>
            </td>
          </tr>
        ) : rows.length === 0 ? (
          <tr>
            <td colSpan={columnCount}>
              <p className="bodytext-13 u-text-light-gray u-text-center u-ptb16">
                該当するメディアがありません。
              </p>
            </td>
          </tr>
        ) : (
          sortedRows.flatMap((row) => {
            const matches = flattenAcrRowToMatchRows(row.acr_scan_result);
            const aiSummary = formatAiSummary(
              row.acr_scan_result?.ai_detection,
            );
            // 照合 0 件のファイルも 1 行（照合列は空）で表示する。
            const renderRows: (AcrMatchRow | null)[] =
              matches.length === 0 ? [null] : matches;

            // 照合中の行もモーダルを開けるようにする（個別再照合の入口）。
            return renderRows.map((match, matchIndex) => (
              <ToolTip
                key={`${row.file_id}-${matchIndex}`}
                description="クリックで詳細を表示"
              >
                <tr
                  onClick={() => onRowClick(row)}
                  style={{ cursor: "pointer" }}
                >
                  {showShotName && (
                    <ToolTip description={row.shot_name ?? ""} truncatedOnly>
                      <td>
                        <span className="bodytext-13" translate="no">
                          {row.shot_name ?? ""}
                        </span>
                      </td>
                    </ToolTip>
                  )}
                  <td>
                    <span className="bodytext-13 u-text-light-gray">
                      <DateDisplay
                        date={row.uploaded_at}
                        type={DATE_DISPLAY_TYPE.DATE_TIME}
                      />
                    </span>
                  </td>
                  <ToolTip description={row.file_name} truncatedOnly>
                    <td>
                      <span className="bodytext-13">{row.file_name}</span>
                    </td>
                  </ToolTip>
                  <td>
                    <AcrStatusCell status={row.acr_scan_status} />
                  </td>
                  <td>
                    <span className="bodytext-13">
                      {match?.category ?? ""}
                    </span>
                  </td>
                  <ToolTip description={match?.title ?? ""} truncatedOnly>
                    <td>
                      <span className="bodytext-13">
                        {match?.title ?? ""}
                      </span>
                    </td>
                  </ToolTip>
                  <ToolTip description={match?.artists ?? ""} truncatedOnly>
                    <td>
                      <span className="bodytext-13">
                        {match?.artists ?? ""}
                      </span>
                    </td>
                  </ToolTip>
                  <td>
                    <span className="bodytext-13">{match?.score ?? ""}</span>
                  </td>
                  <ToolTip description={match?.isrc ?? ""} truncatedOnly>
                    <td>
                      <span className="bodytext-13">{match?.isrc ?? ""}</span>
                    </td>
                  </ToolTip>
                  <td>
                    <span className="bodytext-13 u-text-light-gray">
                      {match?.timeRange ?? ""}
                    </span>
                  </td>
                  <td>
                    <span className="bodytext-13 u-text-light-gray">
                      {match?.duration ?? ""}
                    </span>
                  </td>
                  <ToolTip description={aiSummary} truncatedOnly>
                    <td>
                      <span className="bodytext-13">{aiSummary}</span>
                    </td>
                  </ToolTip>
                </tr>
              </ToolTip>
            ));
          })
        )}
      </Table>
    </section>
  );
};

export default AcrResultsTables;
